import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import SectionHeader from './SectionHeader';

// Shared between the Control Panel index and its sub-pages
// (SupplierAutomation.jsx, NextActions.jsx, CustomerBlacklist.jsx,
// BrandExclusion.jsx).

export const textInputStyle = {
  width: '100%', background: 'var(--bg3)', color: 'var(--white)',
  border: '1px solid var(--border2)', borderRadius: 4,
  padding: '4px 8px', fontFamily: 'var(--font-body)', fontSize: 13,
};

export const SupplierAutomationBox = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState({}); // { [id]: true } while a toggle is in flight

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('suppliers')
      .select('id, supplier_name, auto_stock_refresh_active')
      .order('supplier_name');
    if (error) {
      console.error('Failed to load suppliers:', error);
      setSuppliers([]);
    } else {
      setSuppliers(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id, field, currentValue) => {
    setSaving(prev => ({ ...prev, [id]: true }));
    const { error } = await supabase
      .from('suppliers')
      .update({ [field]: !currentValue })
      .eq('id', id);
    setSaving(prev => ({ ...prev, [id]: false }));
    if (error) {
      alert(`Failed to update: ${error.message}`);
      return;
    }
    setSuppliers(prev => prev.map(s => s.id === id ? { ...s, [field]: !currentValue } : s));
  };

  return (
    <section className="section">
      <SectionHeader
        title="Supplier Automation"
        count={suppliers.length}
        onRefresh={load}
        loading={loading}
      />
      <p className="section__desc">
        Turn Auto Stock Refresh on or off per supplier — an easy manual override without
        touching code. Checked as the first step by the Bringans and Geek Culture stock
        refresh scripts; off means they place no order.
      </p>

      {loading && <div className="loading-row">Loading…</div>}

      {!loading && (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Supplier</th>
              <th style={{ textAlign: 'center' }}>Auto Stock Refresh Active</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.id}>
                <td>{s.supplier_name}</td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={!!s.auto_stock_refresh_active}
                    disabled={!!saving[s.id]}
                    onChange={() => toggle(s.id, 'auto_stock_refresh_active', s.auto_stock_refresh_active)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

export const NextActionsBox = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState({}); // { [id]: true } while a field save is in flight
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('blocked_order_next_actions')
      .select('id, label, sort_order, active')
      .order('sort_order');
    if (error) {
      console.error('Failed to load next actions:', error);
      setActions([]);
    } else {
      setActions(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateField = async (id, field, value) => {
    setSaving(prev => ({ ...prev, [id]: true }));
    const { error } = await supabase
      .from('blocked_order_next_actions')
      .update({ [field]: value })
      .eq('id', id);
    setSaving(prev => ({ ...prev, [id]: false }));
    if (error) {
      alert(`Failed to update: ${error.message}`);
      return;
    }
    setActions(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setAdding(true);
    const nextSort = actions.length ? Math.max(...actions.map(a => a.sort_order)) + 1 : 1;
    const { data, error } = await supabase
      .from('blocked_order_next_actions')
      .insert({ label: newLabel.trim(), sort_order: nextSort, active: true })
      .select()
      .single();
    setAdding(false);
    if (error) {
      alert(`Failed to add: ${error.message}`);
      return;
    }
    setActions(prev => [...prev, data]);
    setNewLabel('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this next action?')) return;
    const { error } = await supabase
      .from('blocked_order_next_actions')
      .delete()
      .eq('id', id);
    if (error) {
      alert(`Failed to delete — likely still referenced by existing notes. Deactivate it instead.\n\n${error.message}`);
      return;
    }
    setActions(prev => prev.filter(a => a.id !== id));
  };

  return (
    <section className="section">
      <SectionHeader
        title="Blocked Order — Next Actions"
        count={actions.length}
        onRefresh={load}
        loading={loading}
      />
      <p className="section__desc">
        Manage the options shown in the "Next Action" dropdown on Blocked Orders / Follow-Up Schedule.
        Inactive actions stay on past notes but drop out of the dropdown. Sort order controls the
        dropdown's listed order (lowest first).
      </p>

      {loading && <div className="loading-row">Loading…</div>}

      {!loading && (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Label</th>
              <th style={{ width: 100 }}>Sort Order</th>
              <th style={{ textAlign: 'center', width: 80 }}>Active</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {actions.map(a => (
              <tr key={a.id}>
                <td>
                  <input
                    type="text"
                    defaultValue={a.label}
                    disabled={!!saving[a.id]}
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val && val !== a.label) updateField(a.id, 'label', val);
                    }}
                    style={textInputStyle}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    defaultValue={a.sort_order}
                    disabled={!!saving[a.id]}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!Number.isNaN(val) && val !== a.sort_order) updateField(a.id, 'sort_order', val);
                    }}
                    style={textInputStyle}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={!!a.active}
                    disabled={!!saving[a.id]}
                    onChange={() => updateField(a.id, 'active', !a.active)}
                  />
                </td>
                <td>
                  <button
                    className="action-btn action-btn--ghost"
                    style={{ fontSize: 11 }}
                    onClick={() => handleDelete(a.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td>
                <input
                  type="text"
                  placeholder="New next action…"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                  style={textInputStyle}
                />
              </td>
              <td colSpan={2} />
              <td>
                <button
                  className="action-btn action-btn--close"
                  disabled={!newLabel.trim() || adding}
                  onClick={handleAdd}
                >
                  {adding ? '…' : 'Add'}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
};

export const BlacklistBox = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState({}); // { [id]: true } while a field save is in flight
  const [newEntry, setNewEntry] = useState({ first_name: '', last_name: '', email: '', rationale: '' });
  const [adding, setAdding]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('customer_blacklist')
      .select('id, first_name, last_name, email, rationale, created_at')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Failed to load blacklist:', error);
      setEntries([]);
    } else {
      setEntries(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateField = async (id, field, value) => {
    setSaving(prev => ({ ...prev, [id]: true }));
    const { error } = await supabase
      .from('customer_blacklist')
      .update({ [field]: value || null })
      .eq('id', id);
    setSaving(prev => ({ ...prev, [id]: false }));
    if (error) {
      alert(`Failed to update: ${error.message}`);
      return;
    }
    setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value || null } : e));
  };

  const canAdd = (newEntry.first_name.trim() && newEntry.last_name.trim()) || newEntry.email.trim();

  const handleAdd = async () => {
    if (!canAdd) return;
    setAdding(true);
    const { data, error } = await supabase
      .from('customer_blacklist')
      .insert({
        first_name: newEntry.first_name.trim() || null,
        last_name:  newEntry.last_name.trim()  || null,
        email:      newEntry.email.trim()      || null,
        rationale:  newEntry.rationale.trim()  || null,
      })
      .select()
      .single();
    setAdding(false);
    if (error) {
      alert(`Failed to add: ${error.message}`);
      return;
    }
    setEntries(prev => [data, ...prev]);
    setNewEntry({ first_name: '', last_name: '', email: '', rationale: '' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blacklist entry?')) return;
    const { error } = await supabase
      .from('customer_blacklist')
      .delete()
      .eq('id', id);
    if (error) {
      alert(`Failed to delete: ${error.message}`);
      return;
    }
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  return (
    <section className="section">
      <SectionHeader
        title="Customer Blacklist"
        count={entries.length}
        onRefresh={load}
        loading={loading}
      />
      <p className="section__desc">
        Sales orders from a matching customer (delivery first + last name, or email) are automatically
        moved to "Problem Encountered" in Cin7 by check_sales_order_blacklist.py, with a note
        on Blocked Orders explaining why. A row needs at least a first + last name, or an email,
        to ever match anything.
      </p>

      {loading && <div className="loading-row">Loading…</div>}

      {!loading && (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Rationale</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id}>
                <td>
                  <input
                    type="text"
                    defaultValue={e.first_name || ''}
                    disabled={!!saving[e.id]}
                    onBlur={(ev) => {
                      const val = ev.target.value.trim();
                      if (val !== (e.first_name || '')) updateField(e.id, 'first_name', val);
                    }}
                    style={textInputStyle}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    defaultValue={e.last_name || ''}
                    disabled={!!saving[e.id]}
                    onBlur={(ev) => {
                      const val = ev.target.value.trim();
                      if (val !== (e.last_name || '')) updateField(e.id, 'last_name', val);
                    }}
                    style={textInputStyle}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    defaultValue={e.email || ''}
                    disabled={!!saving[e.id]}
                    onBlur={(ev) => {
                      const val = ev.target.value.trim();
                      if (val !== (e.email || '')) updateField(e.id, 'email', val);
                    }}
                    style={textInputStyle}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    defaultValue={e.rationale || ''}
                    disabled={!!saving[e.id]}
                    onBlur={(ev) => {
                      const val = ev.target.value.trim();
                      if (val !== (e.rationale || '')) updateField(e.id, 'rationale', val);
                    }}
                    style={textInputStyle}
                  />
                </td>
                <td>
                  <button
                    className="action-btn action-btn--ghost"
                    style={{ fontSize: 11 }}
                    onClick={() => handleDelete(e.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td>
                <input
                  type="text"
                  placeholder="First name…"
                  value={newEntry.first_name}
                  onChange={(ev) => setNewEntry(prev => ({ ...prev, first_name: ev.target.value }))}
                  style={textInputStyle}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Last name…"
                  value={newEntry.last_name}
                  onChange={(ev) => setNewEntry(prev => ({ ...prev, last_name: ev.target.value }))}
                  style={textInputStyle}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Email…"
                  value={newEntry.email}
                  onChange={(ev) => setNewEntry(prev => ({ ...prev, email: ev.target.value }))}
                  style={textInputStyle}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Rationale…"
                  value={newEntry.rationale}
                  onChange={(ev) => setNewEntry(prev => ({ ...prev, rationale: ev.target.value }))}
                  onKeyDown={(ev) => { if (ev.key === 'Enter') handleAdd(); }}
                  style={textInputStyle}
                />
              </td>
              <td>
                <button
                  className="action-btn action-btn--close"
                  disabled={!canAdd || adding}
                  onClick={handleAdd}
                >
                  {adding ? '…' : 'Add'}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
};

export const BrandExclusionBox = () => {
  const [entries, setEntries]     = useState([]);
  const [suppliers, setSuppliers] = useState([]); // { id, supplier_name, cin7_member_id }
  const [loading, setLoading]     = useState(true);
  const [newSupplierId, setNewSupplierId] = useState('');
  const [newBrand, setNewBrand]           = useState('');
  const [brandOptions, setBrandOptions]   = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [adding, setAdding]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: exclusions, error: exErr }, { data: sups, error: supErr }] = await Promise.all([
      supabase.from('brand_exclusion').select('id, supplier_id, brand, created_at').order('created_at', { ascending: false }),
      supabase.from('suppliers').select('id, supplier_name, cin7_member_id').not('cin7_member_id', 'is', null).order('supplier_name'),
    ]);
    if (exErr) console.error('Failed to load brand exclusions:', exErr);
    if (supErr) console.error('Failed to load suppliers:', supErr);
    setEntries(exclusions || []);
    setSuppliers(sups || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // supplier_id on brand_exclusion is the Cin7 member id (matches products.supplier_id),
  // not suppliers.id -- look up display name via cin7_member_id.
  const supplierName = (cin7MemberId) =>
    suppliers.find(s => s.cin7_member_id === cin7MemberId)?.supplier_name || `Supplier ${cin7MemberId}`;

  useEffect(() => {
    setNewBrand('');
    if (!newSupplierId) { setBrandOptions([]); return; }
    setLoadingBrands(true);
    supabase
      .from('v_active_products')
      .select('brand')
      .eq('supplier_id', Number(newSupplierId))
      .then(({ data, error }) => {
        if (error) {
          console.error('Failed to load brands:', error);
          setBrandOptions([]);
        } else {
          const distinct = [...new Set((data || []).map(r => (r.brand || '').trim()).filter(Boolean))].sort();
          setBrandOptions(distinct);
        }
        setLoadingBrands(false);
      });
  }, [newSupplierId]);

  const handleAdd = async () => {
    if (!newSupplierId || !newBrand) return;
    setAdding(true);
    const { data, error } = await supabase
      .from('brand_exclusion')
      .insert({ supplier_id: Number(newSupplierId), brand: newBrand })
      .select()
      .single();
    setAdding(false);
    if (error) {
      alert(`Failed to add: ${error.message}`);
      return;
    }
    setEntries(prev => [data, ...prev]);
    setNewBrand('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this brand exclusion?')) return;
    const { error } = await supabase.from('brand_exclusion').delete().eq('id', id);
    if (error) {
      alert(`Failed to delete: ${error.message}`);
      return;
    }
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  return (
    <section className="section">
      <SectionHeader
        title="Brand Exclusion"
        count={entries.length}
        onRefresh={load}
        loading={loading}
      />
      <p className="section__desc">
        Brands excluded from a supplier's restock calculator output, matched against the
        product's brand for that supplier (case-insensitive). Currently read by the Bringans
        reorder calculator; other suppliers' calculators can be wired up to check it the same way.
      </p>

      {loading && <div className="loading-row">Loading…</div>}

      {!loading && (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Supplier</th>
              <th>Brand</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id}>
                <td>{supplierName(e.supplier_id)}</td>
                <td>{e.brand}</td>
                <td>
                  <button
                    className="action-btn action-btn--ghost"
                    style={{ fontSize: 11 }}
                    onClick={() => handleDelete(e.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td>
                <select
                  value={newSupplierId}
                  onChange={(e) => setNewSupplierId(e.target.value)}
                  style={textInputStyle}
                >
                  <option value="">Select supplier…</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.cin7_member_id}>{s.supplier_name}</option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={newBrand}
                  onChange={(e) => setNewBrand(e.target.value)}
                  disabled={!newSupplierId || loadingBrands}
                  style={textInputStyle}
                >
                  <option value="">{loadingBrands ? 'Loading brands…' : 'Select brand…'}</option>
                  {brandOptions.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </td>
              <td>
                <button
                  className="action-btn action-btn--close"
                  disabled={!newSupplierId || !newBrand || adding}
                  onClick={handleAdd}
                >
                  {adding ? '…' : 'Add'}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
};
